import React, { useCallback, useEffect, useMemo, useState } from "react";
import { feedbackApi, meApi, messagingEndpointsApi, onboardingApi } from "../api/routes";
import { apiErrorMessage } from "../api/client";
import { MascotSprite } from "../components/BrandArtwork";
import { Button, Card, Input, Pill, Select, TextArea, Toast } from "../components/ui/Controls";
import TimeZoneCombo from "../components/ui/TimeZoneCombo";
import { usePanelCache } from "../state/PanelCacheContext";
import { DISTANCE_TYPE_OPTIONS, distanceTypeLabel, normalizeDistanceType } from "../domain/distanceTypes";
import planWizardArtwork from "../assets/brand/chara-plan-wizard-v1.png";

const DAYS = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
const FEEDBACK_TYPES = ["COACHING", "WEBSITE", "MESSAGE", "PLAN", "INFRASTRUCTURE", "BILLING", "ONBOARDING", "OTHER"];
const FEEDBACK_SEVERITIES = ["LOW", "MEDIUM", "HIGH", "CRITICAL"];
const DRAFT_KEY = "chara:user-plan-draft:v1";
const KM_PER_MI = 1.609344;

function normalizePhone(value) {
  const cleaned = (value || "").trim();
  if (!cleaned) return "";
  const digits = cleaned.replace(/[^\d+]/g, "");
  if (digits.startsWith("+")) return digits;
  if (digits.length === 10) return `+1${digits}`;
  return digits;
}

function smsEndpointId(phone) {
  const digits = normalizePhone(phone).replace(/\D/g, "");
  return digits ? `sms-${digits}` : "";
}

function endpointChannel(endpoint) {
  return (endpoint?.channel || "").toUpperCase();
}

function endpointLabel(endpoint) {
  const channel = endpointChannel(endpoint);
  if (channel === "SMS") return endpoint?.externalId || "SMS number";
  if (channel === "TELEGRAM") return endpoint?.externalId ? `Telegram ${endpoint.externalId}` : "Telegram chat";
  return endpoint?.externalId || endpoint?.endpointId || "Messaging endpoint";
}

const emptyPlanDraft = {
  name: "",
  personaId: "",
  philosophyId: "",
  startDate: "",
  status: "draft",
  goalName: "",
  goalDate: "",
  targetTime: "",
  pace: "",
  raceName: "",
  raceDistance: "MARATHON",
};

const emptyWorkoutLog = {
  manualEntryId: "",
  personalizedWorkoutId: "",
  personalizedWorkoutVersion: "",
  planId: "",
  weekIndex: "",
  plannedDate: "",
  completionStatus: "COMPLETED",
  name: "",
  startedAt: "",
  actualDistanceKm: "",
  actualPace: "",
  actualDuration: "",
  movingTimeSeconds: "",
  elevationGainM: "",
  avgHrBpm: "",
  athleteNotes: "",
  skipReason: "",
  intervals: [],
};

const emptyFeedbackForm = {
  type: "COACHING",
  severity: "LOW",
  title: "",
  message: "",
};

const PLAN_WIZARD_STEPS = [
  {
    key: "foundation",
    title: "Start with the training arc",
    helper: "Name the block and choose when it begins. A clear start date helps Chara shape the weekly progression around your life.",
    fields: ["name", "startDate"],
  },
  {
    key: "goal",
    title: "Give your coach the target",
    helper: "Tell Chara what you are aiming for. Distance and race date anchor the calendar; the race name is optional but useful.",
    fields: ["raceDistance", "goalName", "raceName", "goalDate"],
  },
  {
    key: "coach",
    title: "Choose your coach",
    helper: "Pick the coaching voice and doctrine level for this block. Chara will use that persona's race-distance philosophy unless you choose a specific override.",
    fields: ["personaId"],
  },
  {
    key: "execution",
    title: "Choose how to get there",
    helper: "Chara uses your account persona's philosophy for this distance unless you choose an override. Add target time or pace when it matters.",
    fields: ["philosophyId", "targetTime", "pace"],
  },
];
const planBuildSignals = [
  "Reading your athlete profile",
  "Balancing goal, recovery, and schedule",
  "Drafting the first training block",
];

const PERSONA_CARD_META = {
  "gandalf-beginner": {
    level: "Beginner",
    image: "/personas/alder-coach.png",
    tagline: "Patient, durable, confidence-first coaching.",
    detail: "Best for first blocks, first races, return-to-running seasons, or anyone who wants the start line protected.",
  },
  "aragorn-intermediate": {
    level: "Intermediate",
    image: "/personas/rowan-coach.png",
    tagline: "Strategic training with balanced pressure.",
    detail: "Best when you can train consistently and want the limiter found without turning every run medium-hard.",
  },
  "galadriel-advanced": {
    level: "Advanced",
    image: "/personas/elowen-coach.png",
    tagline: "Precise, demanding, and unsentimental.",
    detail: "Best for experienced athletes who can absorb specificity, execute cleanly, and recover honestly.",
  },
};

function personaMeta(persona) {
  return PERSONA_CARD_META[persona?.personaId] || {
    level: "Custom",
    image: planWizardArtwork,
    tagline: "Custom Chara coaching voice.",
    detail: "Uses the configured persona prompt overrides and distance philosophy map.",
  };
}

function personaSupportsDistance(persona, distance) {
  if (!persona || !distance) return false;
  return Boolean(persona.philosophyIdsByDistance?.[distance]);
}

function philosophyForDistance(philosophy, distance) {
  return !distance || !philosophy?.distanceType || philosophy.distanceType === distance;
}

function linesToList(value) {
  return (value || "")
    .split(/\n+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function listToLines(value) {
  return Array.isArray(value) ? value.join("\n") : "";
}

function listValue(value) {
  if (Array.isArray(value)) return value;
  return linesToList(value);
}

function compactList(value) {
  return listValue(value)
    .map((item) => String(item || "").trim())
    .filter(Boolean);
}

function preferenceRecords(value) {
  return compactList(value).map((summary) => ({
    normalizedKey: `TRAINING|${summary.toLowerCase().replace(/\s+/g, " ")}`,
    summary,
    scope: "TRAINING",
    polarity: /^(avoid|dislike)|do not like/i.test(summary) ? "AVOIDS" : "PREFERS",
    strength: "MODERATE",
    status: "ACTIVE",
    confidence: 1,
  }));
}

function compactPersonalRecords(records) {
  return (Array.isArray(records) ? records : [])
    .map((record) => ({
      distance: normalizeDistanceType(record.distance),
      time: String(record.time || "").trim(),
      date: String(record.date || "").trim() || null,
      context: String(record.context || "").trim() || null,
    }))
    .filter((record) => record.distance && record.time);
}

function dayKeyVariants(day) {
  return [day, day.toLowerCase(), day.slice(0, 3), day.slice(0, 3).toLowerCase()];
}

function availabilityValue(availability, day) {
  const source = availability || {};
  const key = dayKeyVariants(day).find((variant) => source[variant] !== undefined);
  return key ? source[key] : "";
}

function withAvailabilityDay(availability, day, value) {
  const next = { ...(availability || {}) };
  dayKeyVariants(day).forEach((variant) => {
    if (variant !== day) delete next[variant];
  });
  next[day] = value;
  return next;
}

function weekCount(plan) {
  return Array.isArray(plan?.weeks) ? plan.weeks.length : 0;
}

function calendarWeekCount(plan) {
  const start = parseDate(plan?.startDate);
  const end = parseDate(planGoalDate(plan));
  if (!start || !end || end < start) return weekCount(plan);
  const days = Math.floor((end.getTime() - start.getTime()) / 86400000) + 1;
  return Math.max(1, Math.ceil(days / 7));
}

function workoutLabel(workout) {
  return workout?.name || workout?.type || "Workout";
}

function isAutoCompletedWorkout(workout) {
  return Boolean(workout?.autoCompleted || workout?.requiresCompletion === false);
}

function displayDistanceUnit(source, fallback = "mi") {
  return source?.distanceUnit || fallback;
}

function sourceUnitLabel(unit, fallbackUnit = "mi") {
  if (unit === "METER") return "m";
  if (unit === "KILOMETER") return "km";
  if (unit === "MILE") return "mi";
  return fallbackUnit;
}

function distanceLabel(value, fallbackUnit = "mi") {
  if (!value) return null;
  if (typeof value === "object") {
    if (value.label) return value.label;
    if (value.value !== undefined && value.value !== null) return `${value.value} ${value.unit || fallbackUnit}`;
    if (value.sourceValue !== undefined && value.sourceValue !== null) return `${value.sourceValue} ${sourceUnitLabel(value.sourceUnit, fallbackUnit)}`;
    if (value.meters !== undefined && value.meters !== null) return `${Number((value.meters / 1000).toFixed(2))} km`;
    return null;
  }
  return `${value} ${fallbackUnit}`;
}

function distanceInputValue(value) {
  if (!value) return "";
  if (typeof value === "object") {
    if (value.value !== undefined && value.value !== null) return value.value;
    if (value.sourceValue !== undefined && value.sourceValue !== null) return value.sourceValue;
    if (value.meters !== undefined && value.meters !== null) return Number((value.meters / 1000).toFixed(3));
    return "";
  }
  return value;
}

function workoutDistanceLabel(workout, fallbackUnit = "mi") {
  if (!workout?.estimatedDistance) return null;
  return distanceLabel(workout.estimatedDistance, displayDistanceUnit(workout, fallbackUnit));
}

function actualDistanceLabel(entry, fallbackUnit = "mi") {
  const value = entry?.actualDistance ?? entry?.actualDistanceKm;
  if (!value) return "No distance";
  return distanceLabel(value, displayDistanceUnit(entry, fallbackUnit));
}

function intervalDetail(interval) {
  if (!interval) return "";
  const parts = [];
  if (interval.description) parts.push(interval.description);
  if (interval.actualDistance) parts.push(distanceLabel(interval.actualDistance));
  if (interval.actualDurationSeconds) parts.push(`${Math.round(interval.actualDurationSeconds / 60)} min`);
  if (interval.actualPace) parts.push(interval.actualPace);
  if (interval.avgHrBpm) parts.push(`${Math.round(interval.avgHrBpm)} bpm`);
  if (interval.completed === false) parts.push("not completed");
  return parts.join(" · ") || `Interval ${interval.intervalIndex || ""}`.trim();
}

function workoutDetails(workout) {
  if (!workout) return [];
  return [
    ["Type", workout.type],
    ["Duration", workout.estimatedDuration],
    ["Distance", workoutDistanceLabel(workout)],
    ["Stress", workout.stressScore],
    ["Intensity", workout.intensity],
    ["Difficulty", workout.difficulty],
    ["Benefit", workout.primaryTrainingBenefit],
  ].filter(([, value]) => value !== null && value !== undefined && value !== "");
}

function compactDate(value) {
  if (!value) return "TBD";
  return value.length > 10 ? value.slice(0, 10) : value;
}

function isIsoDate(value) {
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value);
}

function weekdayName(value) {
  const date = parseDate(value);
  return date ? date.toLocaleDateString(undefined, { weekday: "long", timeZone: "UTC" }) : "";
}

function dayLabel(day) {
  if (!day) return "Day";
  if (isIsoDate(day.day)) {
    return weekdayName(day.date || day.day) || compactDate(day.day);
  }
  return day.day;
}

function normalizeMissingWorkoutLabel(value) {
  if (typeof value !== "string") return value;
  const parts = value.trim().split(/\s+/);
  if (parts.length >= 2 && isIsoDate(parts[0]) && parts[0] === parts[1]) {
    return parts.slice(1).join(" ");
  }
  return value;
}

function parseDate(value) {
  if (!value) return null;
  const date = new Date(value.length > 10 ? value : `${value}T00:00:00Z`);
  return Number.isNaN(date.getTime()) ? null : date;
}

function planGoalDate(plan) {
  return plan?.goal?.date || plan?.goal?.race?.date || plan?.raceDate || "";
}

function planProgressByGoalDate(plan) {
  const start = parseDate(plan?.startDate);
  const end = parseDate(planGoalDate(plan));
  if (!start || !end || end <= start) {
    return null;
  }
  const now = new Date();
  const pct = Math.round(((now.getTime() - start.getTime()) / (end.getTime() - start.getTime())) * 100);
  return Math.max(0, Math.min(100, pct));
}

function comparePlansByStartDate(a, b) {
  const aTime = parseDate(a?.startDate)?.getTime() ?? 0;
  const bTime = parseDate(b?.startDate)?.getTime() ?? 0;
  return aTime - bTime;
}

function numberOrUndefined(value) {
  return value === "" || value === null || value === undefined ? undefined : Number(value);
}

function toKilometers(value, displayUnit) {
  if (value && typeof value === "object" && value.meters !== undefined && value.meters !== null) {
    return Number((value.meters / 1000).toFixed(3));
  }
  const parsed = numberOrUndefined(value);
  if (parsed === undefined) return undefined;
  return displayUnit === "mi" ? Number((parsed * KM_PER_MI).toFixed(3)) : parsed;
}

export default function UserDashboardPanel({ activeTab = "dashboard", onTabChange }) {
  const [panelState, setPanelState] = usePanelCache("userDashboard");
  const [data, setDataState] = useState(panelState.data || null);
  const [selectedWeek, setSelectedWeek] = useState(panelState.selectedWeek || 0);
  const [weekView, setWeekViewState] = useState(panelState.weekView || null);
  const [userForm, setUserForm] = useState(panelState.userForm || {});
  const [athleteForm, setAthleteForm] = useState(panelState.athleteForm || { trainingExperience: "", preferences: [], personalRecords: [], maxMileage: "", availability: {}, trainingBias: "NORMAL" });
  const [planDraft, setPlanDraft] = useState(() => {
    try {
      return { ...emptyPlanDraft, ...JSON.parse(localStorage.getItem(DRAFT_KEY) || "{}") };
    } catch {
      return emptyPlanDraft;
    }
  });
  const [philosophies, setPhilosophiesState] = useState(panelState.philosophies || []);
  const [personas, setPersonasState] = useState(panelState.personas || []);
  const [waitingJob, setWaitingJob] = useState(panelState.waitingJob || null);
  const [completionData, setCompletionDataState] = useState(panelState.completionData || { completedWorkouts: [], manualWorkouts: [], adaptation: null });
  const [messagingEndpoints, setMessagingEndpoints] = useState(panelState.messagingEndpoints || []);
  const [smsDraft, setSmsDraft] = useState(panelState.smsDraft || { phone: "", consent: false });
  const [telegramLink, setTelegramLink] = useState(null);
  const [messagingBusy, setMessagingBusy] = useState(false);
  const [recordingWorkout, setRecordingWorkout] = useState(null);
  const [workoutLog, setWorkoutLog] = useState(emptyWorkoutLog);
  const [feedbackForm, setFeedbackForm] = useState(panelState.feedbackForm || emptyFeedbackForm);
  const [feedbackRecords, setFeedbackRecords] = useState(panelState.feedbackRecords || []);
  const [loading, setLoading] = useState(!panelState.data);
  const [loadError, setLoadError] = useState("");
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const [planWizardOpen, setPlanWizardOpen] = useState(false);
  const [planWizardStep, setPlanWizardStep] = useState(0);
  const tab = activeTab;

  const currentPlan = data?.currentWorkoutPlan;
  const currentPlanId = currentPlan?.planId;
  const profile = data?.athleteProfile;
  const athleteMemory = data?.athleteMemory;
  const latestTrainingReview = data?.trainingReviews?.[0];
  const totalWeeks = weekCount(currentPlan);
  const currentWeekIndex = Math.max(0, data?.currentWeek?.weekIndex || 0);
  const goalProgress = planProgressByGoalDate(currentPlan);
  const progress = goalProgress ?? (totalWeeks ? Math.min(100, Math.round(((currentWeekIndex + 1) / totalWeeks) * 100)) : 0);
  const adaptation = completionData?.adaptation;
  const [planDraftSavedAt, setPlanDraftSavedAt] = useState(null);
  const [selectedWorkoutDetail, setSelectedWorkoutDetail] = useState(null);

  const terminalPlan = (plan) => ["completed", "ended"].includes(String(plan?.status || "").toLowerCase());

  const setCompletionData = useCallback((nextCompletionData) => {
    setCompletionDataState(nextCompletionData);
    setPanelState({ completionData: nextCompletionData });
  }, [setPanelState]);

  const completionByWorkoutId = useMemo(() => {
    const map = new Map();
    (completionData?.completedWorkouts || []).forEach((workout) => {
      if (workout?.personalizedWorkoutId) map.set(workout.personalizedWorkoutId, workout);
    });
    return map;
  }, [completionData?.completedWorkouts]);

  const dashboardStats = useMemo(() => [
    { label: "Plan progress", value: currentPlan ? `${progress}%` : "No plan" },
    { label: "Current week", value: totalWeeks ? `${currentWeekIndex + 1} / ${totalWeeks}` : "TBD" },
    { label: "Goal date", value: planGoalDate(currentPlan) ? compactDate(planGoalDate(currentPlan)) : "TBD" },
    { label: "Training bias", value: profile?.trainingBias || "NORMAL" },
  ], [currentPlan, currentWeekIndex, profile?.trainingBias, progress, totalWeeks]);

  const messagingByChannel = useMemo(() => ({
    TELEGRAM: messagingEndpoints.filter((endpoint) => endpointChannel(endpoint) === "TELEGRAM"),
    SMS: messagingEndpoints.filter((endpoint) => endpointChannel(endpoint) === "SMS"),
  }), [messagingEndpoints]);

  const preferredChannel = (userForm.preferredMessagingChannel || data?.user?.preferredMessagingChannel || "").toUpperCase();

  const setData = useCallback((nextData) => {
    setDataState(nextData);
    setPanelState({ data: nextData });
  }, [setPanelState]);

  const setWeekView = useCallback((nextWeekView) => {
    setWeekViewState(nextWeekView);
    setPanelState({ weekView: nextWeekView });
  }, [setPanelState]);

  const setPhilosophies = useCallback((nextPhilosophies) => {
    setPhilosophiesState(nextPhilosophies);
    setPanelState({ philosophies: nextPhilosophies });
  }, [setPanelState]);

  const setPersonas = useCallback((nextPersonas) => {
    setPersonasState(nextPersonas);
    setPanelState({ personas: nextPersonas });
  }, [setPanelState]);

  const loadMe = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const response = await meApi.get();
      setData(response.data);
      return response.data;
    } catch (error) {
      const message = apiErrorMessage(error);
      setLoadError(message);
      setToast({ tone: "error", message });
      return null;
    } finally {
      setLoading(false);
    }
  }, [setData]);

  const loadPlanOptions = useCallback(async () => {
    try {
      const response = await meApi.planOptions();
      setPhilosophies(response.data?.philosophies || []);
      setPersonas(response.data?.personas || []);
    } catch {
      setPhilosophies([]);
      setPersonas([]);
    }
  }, [setPhilosophies, setPersonas]);

  const loadMessagingEndpoints = useCallback(async (userId) => {
    if (!userId) {
      setMessagingEndpoints([]);
      return;
    }
    try {
      const response = await messagingEndpointsApi.listByUser(userId);
      setMessagingEndpoints(response.data || []);
    } catch (error) {
      if (error?.response?.status === 403) {
        setMessagingEndpoints([]);
        return;
      }
      setToast({ tone: "error", message: apiErrorMessage(error, "Unable to load messaging connections") });
    }
  }, []);

  useEffect(() => {
    if (!panelState.data) {
      loadMe();
    }
    if (!panelState.philosophies?.length || !panelState.personas?.length) {
      loadPlanOptions();
    }
  }, [loadMe, loadPlanOptions, panelState.data, panelState.philosophies?.length, panelState.personas?.length]);

  useEffect(() => {
    if (data?.user?.userId) {
      loadMessagingEndpoints(data.user.userId);
    }
  }, [data?.user?.userId, loadMessagingEndpoints]);

  useEffect(() => {
    setPanelState({
      data,
      selectedWeek,
      weekView,
      userForm,
      athleteForm,
      philosophies,
      personas,
      waitingJob,
      completionData,
      messagingEndpoints,
      smsDraft,
    });
  }, [data, selectedWeek, weekView, userForm, athleteForm, philosophies, personas, waitingJob, completionData, messagingEndpoints, smsDraft, setPanelState]);

  useEffect(() => {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(planDraft));
  }, [planDraft]);

  useEffect(() => {
    if (planDraft.personaId || !personas.length) return;
    const defaultPersona = personas.find((persona) => personaSupportsDistance(persona, planDraft.raceDistance)) || personas[0];
    if (defaultPersona?.personaId) {
      setPlanDraft((current) => current.personaId ? current : { ...current, personaId: defaultPersona.personaId });
    }
  }, [personas, planDraft.personaId, planDraft.raceDistance]);

  useEffect(() => {
    if (!data) return;
    setUserForm({ ...(data.user || {}), measurementSystem: data.user?.measurementSystem || data.measurementSystem || "IMPERIAL" });
    setSmsDraft((current) => ({ ...current, phone: data.user?.phone || current.phone || "" }));
    setAthleteForm({
      trainingExperience: listToLines(data.athleteProfile?.trainingExperience),
      preferences: (data.athleteMemory?.preferences || []).map((preference) => preference.summary).filter(Boolean),
      personalRecords: data.athleteProfile?.personalRecords || [],
      maxMileage: data.athleteProfile?.maxMileage || "",
      availability: data.athleteProfile?.availability || {},
      trainingBias: data.athleteProfile?.trainingBias || "NORMAL",
    });
    setSelectedWeek(data.currentWeek?.weekIndex || 0);
    setWeekView(data.currentWeek || null);
  }, [data, setWeekView]);

  const loadCompletedWorkouts = useCallback(async () => {
    try {
      const response = await meApi.completedWorkouts();
      setCompletionData(response.data || { completedWorkouts: [], manualWorkouts: [], adaptation: null });
    } catch (error) {
      if (error?.response?.status === 401 || error?.response?.status === 403) {
        setCompletionData({ completedWorkouts: [], manualWorkouts: [], adaptation: null, unavailable: true });
        return;
      }
      setToast({ tone: "error", message: apiErrorMessage(error) });
    }
  }, [setCompletionData]);

  const loadWeek = useCallback(async (planId, weekIndex) => {
    setSelectedWeek(weekIndex);
    if (!planId) return;
    try {
      const response = await meApi.week(planId, weekIndex);
      setWeekView(response.data);
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    }
  }, [setWeekView]);

  useEffect(() => {
    if (!waitingJob?.jobGroupId) return undefined;
    const timer = setInterval(async () => {
      try {
        const response = await meApi.jobStatus(waitingJob.jobGroupId);
        const status = response.data?.status;
        setWaitingJob({ ...response.data, kind: waitingJob.kind });
        if (status === "COMPLETE" || status === "COMPLETE_WITH_ERRORS" || status === "FAILED" || status === "TIMED_OUT") {
          clearInterval(timer);
          if (status === "COMPLETE") {
            if (waitingJob.kind === "adaptation") {
              await loadMe();
              await loadCompletedWorkouts();
              setWaitingJob(null);
            } else {
              localStorage.removeItem(DRAFT_KEY);
              setPlanDraft(emptyPlanDraft);
              const nextData = await loadMe();
              const nextPlan = nextData?.currentWorkoutPlan;
              setSelectedWeek(0);
              if (nextPlan?.planId) {
                await loadWeek(nextPlan.planId, 0);
              } else {
                setWeekView(null);
              }
              setWaitingJob(null);
              setPlanWizardOpen(false);
              onTabChange?.("plans");
            }
          }
        }
      } catch (error) {
        setToast({ tone: "error", message: apiErrorMessage(error) });
      }
    }, 3000);
    return () => clearInterval(timer);
  }, [waitingJob, waitingJob?.jobGroupId, loadMe, loadWeek, loadCompletedWorkouts, onTabChange, setWeekView]);

  useEffect(() => {
    if (data?.currentWorkoutPlan) {
      loadCompletedWorkouts();
    }
  }, [currentPlanId, data?.currentWorkoutPlan, loadCompletedWorkouts]);

  async function saveUser() {
    setSaving(true);
    try {
      const response = await meApi.updateUser(userForm);
      setData(response.data);
      setToast({ message: "User settings saved" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function finishPlan(completed) {
    if (!currentPlan?.planId) return;
    const verb = completed ? "finish" : "end early";
    if (!window.confirm(`Are you sure you want to ${verb} this training plan?`)) return;
    setSaving(true);
    try {
      const response = await meApi.completeWorkoutPlan(currentPlan.planId, completed);
      const groupId = response.data?.jobGroupId;
      if (groupId) setWaitingJob({ jobGroupId: groupId, kind: "plan-completion" });
      setToast({ message: completed ? "Finishing your plan and preparing its summary" : "Ending your plan and preparing its summary" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function createSmsEndpoint() {
    const userId = data?.user?.userId || userForm.userId;
    const phone = normalizePhone(smsDraft.phone || userForm.phone);
    if (!userId) {
      setToast({ tone: "error", message: "User id is required before adding SMS" });
      return;
    }
    if (!phone) {
      setToast({ tone: "error", message: "Add the SMS phone number first" });
      return;
    }
    if (!smsDraft.consent) {
      setToast({ tone: "error", message: "Accept SMS messaging terms before adding SMS" });
      return;
    }
    setMessagingBusy(true);
    try {
      await messagingEndpointsApi.create({
        userId,
        endpointId: smsEndpointId(phone),
        channel: "SMS",
        externalId: phone,
        status: "ACTIVE",
        primaryEndpoint: userForm.preferredMessagingChannel === "SMS",
        verifiedAt: new Date().toISOString(),
        metadataJson: JSON.stringify({
          provider: "TWILIO",
          consent: "RECURRING_SMS",
          consentedAt: new Date().toISOString(),
          source: "DASHBOARD_SETTINGS",
        }),
      });
      if (phone !== userForm.phone) {
        const response = await meApi.updateUser({ ...userForm, phone });
        setData(response.data);
      }
      await loadMessagingEndpoints(userId);
      setToast({ message: "SMS connection saved" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setMessagingBusy(false);
    }
  }

  async function createTelegramConnection() {
    setMessagingBusy(true);
    try {
      const response = await onboardingApi.createTelegramLink();
      setTelegramLink(response.data || null);
      setToast({ message: "Telegram setup link ready" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setMessagingBusy(false);
    }
  }

  async function setPreferredEndpoint(endpoint) {
    const userId = data?.user?.userId || userForm.userId;
    const channel = endpointChannel(endpoint);
    if (!userId || !channel) return;
    setMessagingBusy(true);
    try {
      await Promise.all(
        messagingEndpoints
          .filter((candidate) => endpointChannel(candidate) === channel)
          .map((candidate) => messagingEndpointsApi.update(userId, candidate.endpointId, {
            ...candidate,
            primaryEndpoint: candidate.endpointId === endpoint.endpointId,
          }))
      );
      const response = await meApi.updateUser({ ...userForm, preferredMessagingChannel: channel });
      setData(response.data);
      setUserForm((current) => ({ ...current, preferredMessagingChannel: channel }));
      await loadMessagingEndpoints(userId);
      setToast({ message: `${channel === "SMS" ? "SMS" : "Telegram"} is now preferred` });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setMessagingBusy(false);
    }
  }

  async function deleteMessagingEndpoint(endpoint) {
    const userId = data?.user?.userId || userForm.userId;
    if (!userId || !endpoint?.endpointId) return;
    setMessagingBusy(true);
    try {
      await messagingEndpointsApi.remove(userId, endpoint.endpointId);
      const remaining = messagingEndpoints.filter((candidate) => candidate.endpointId !== endpoint.endpointId);
      const deletedPreferred = userForm.preferredMessagingChannel === endpointChannel(endpoint)
        && !remaining.some((candidate) => endpointChannel(candidate) === endpointChannel(endpoint));
      if (deletedPreferred) {
        const fallback = remaining[0] ? endpointChannel(remaining[0]) : "";
        const response = await meApi.updateUser({ ...userForm, preferredMessagingChannel: fallback });
        setData(response.data);
        setUserForm((current) => ({ ...current, preferredMessagingChannel: fallback }));
      }
      await loadMessagingEndpoints(userId);
      setToast({ message: "Messaging connection deleted" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setMessagingBusy(false);
    }
  }

  async function saveAthlete() {
    setSaving(true);
    try {
      const response = await meApi.updateAthlete({
        athleteProfile: {
          ...profile,
          trainingExperience: linesToList(athleteForm.trainingExperience),
          personalRecords: compactPersonalRecords(athleteForm.personalRecords),
          maxMileage: Number(athleteForm.maxMileage || 0),
          availability: athleteForm.availability,
          trainingBias: athleteForm.trainingBias || "NORMAL",
        },
        athleteMemory: {
          preferences: preferenceRecords(athleteForm.preferences),
        },
      });
      setData(response.data);
      setToast({ message: "Athlete settings saved" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  function saveFeedbackForm(next) {
    setFeedbackForm(next);
    setPanelState({ feedbackForm: next });
  }

  async function submitFeedback() {
    if (!feedbackForm.message.trim()) return;
    setSaving(true);
    try {
      const response = await feedbackApi.create({
        source: "WEBSITE",
        type: feedbackForm.type,
        severity: feedbackForm.severity,
        title: feedbackForm.title,
        message: feedbackForm.message,
        metadata: {
          panel: tab,
          path: window.location.pathname + window.location.search,
        },
      });
      const nextRecords = [response.data, ...feedbackRecords];
      setFeedbackRecords(nextRecords);
      setPanelState({ feedbackRecords: nextRecords, feedbackForm: emptyFeedbackForm });
      setFeedbackForm(emptyFeedbackForm);
      setToast({ message: "Feedback sent" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error, "Unable to send feedback") });
    } finally {
      setSaving(false);
    }
  }

  async function createPlan() {
    if (!planDraft.raceDistance) {
      setToast({ tone: "error", message: "Choose a goal distance" });
      return;
    }
    if (!planDraft.startDate) {
      setToast({ tone: "error", message: "Choose the plan start date" });
      setPlanWizardStep(0);
      return;
    }
    if (!planDraft.goalDate) {
      setToast({ tone: "error", message: "Choose the race date" });
      setPlanWizardStep(1);
      return;
    }
    const selectedPersona = personas.find((persona) => persona.personaId === planDraft.personaId)
      || personas.find((persona) => personaSupportsDistance(persona, planDraft.raceDistance));
    if (!selectedPersona?.personaId) {
      setToast({ tone: "error", message: "Choose a coach for this plan" });
      return;
    }
    if (!personaSupportsDistance(selectedPersona, planDraft.raceDistance)) {
      setToast({ tone: "error", message: `${selectedPersona.coachDisplayName || "This coach"} does not support ${distanceTypeLabel(planDraft.raceDistance)}` });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: planDraft.name || `${distanceTypeLabel(planDraft.raceDistance)} plan`,
        personaId: selectedPersona.personaId,
        startDate: planDraft.startDate,
        status: "draft",
        goal: {
          name: planDraft.goalName,
          date: planDraft.goalDate,
          targetTime: planDraft.targetTime,
          pace: planDraft.pace,
          race: {
            name: planDraft.raceName || planDraft.goalName,
            distance: planDraft.raceDistance,
          },
        },
      };
      if (planDraft.philosophyId) payload.philosophyId = planDraft.philosophyId;
      const response = await meApi.createWorkoutPlan(payload);
      setData(response.data);
      setWaitingJob({ jobGroupId: response.data?.jobGroupId, status: "RUNNING", kind: "plan" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  function savePlanDraft() {
    localStorage.setItem(DRAFT_KEY, JSON.stringify(planDraft));
    setPlanDraftSavedAt(new Date().toISOString());
    setToast({ message: "Plan draft saved" });
  }

  async function saveWorkoutPreference(workoutId, preference, reason) {
    try {
      const response = await meApi.saveWorkoutPreference({ workoutId, preference, ...(reason?.trim() ? { reason: reason.trim() } : {}) });
      setData(response.data);
      setToast({ message: preference === "like" ? "Workout liked" : "Workout disliked" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    }
  }

  async function setCurrentPlan(planId) {
    try {
      const response = await meApi.setCurrentWorkoutPlan({ planId });
      setData(response.data);
      setToast({ message: "Current plan updated" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    }
  }

  function openWorkoutRecorder(workout, day) {
    const existing = (completionData?.manualWorkouts || []).find((entry) => entry.personalizedWorkoutId === workout.workoutId);
    const unit = existing?.distanceUnit || workout?.distanceUnit || data?.distanceUnit || "mi";
    setRecordingWorkout({ workout, day });
    setWorkoutLog({
      ...emptyWorkoutLog,
      ...(existing || {}),
      personalizedWorkoutId: workout.workoutId,
      personalizedWorkoutVersion: workout.version || existing?.personalizedWorkoutVersion || "",
      planId: weekView?.planId || currentPlan?.planId || "",
      weekIndex: weekView?.weekIndex ?? selectedWeek,
      plannedDate: day?.date || existing?.plannedDate || "",
      name: existing?.name || workoutLabel(workout),
      startedAt: existing?.startedAt || (day?.date ? `${day.date}T12:00:00Z` : ""),
      completionStatus: existing?.completionStatus || "COMPLETED",
      actualDistanceKm: distanceInputValue(existing?.actualDistance) || existing?.actualDistanceKm || "",
      distanceUnit: unit,
      actualDuration: existing?.actualDuration || "",
      movingTimeSeconds: existing?.movingTimeSeconds ?? "",
      elevationGainM: existing?.elevationGainM ?? "",
      avgHrBpm: existing?.avgHrBpm ?? "",
      athleteNotes: existing?.athleteNotes || "",
      skipReason: existing?.skipReason || "",
    });
  }

  function workoutPayload() {
    const unit = workoutLog.distanceUnit || recordingWorkout?.workout?.distanceUnit || data?.distanceUnit || "mi";
    const displayDistance = numberOrUndefined(workoutLog.actualDistanceKm);
    const actualDistanceKm = toKilometers(workoutLog.actualDistanceKm, unit);
    return {
      ...workoutLog,
      personalizedWorkoutVersion: numberOrUndefined(workoutLog.personalizedWorkoutVersion),
      weekIndex: numberOrUndefined(workoutLog.weekIndex),
      actualDistance: actualDistanceKm === undefined ? undefined : {
        meters: Math.round(actualDistanceKm * 1000),
        sourceValue: displayDistance,
        sourceUnit: unit === "mi" ? "MILE" : "KILOMETER",
        displayHint: "RAW_SOURCE",
      },
      actualDistanceKm,
      movingTimeSeconds: numberOrUndefined(workoutLog.movingTimeSeconds),
      elevationGainM: numberOrUndefined(workoutLog.elevationGainM),
      avgHrBpm: numberOrUndefined(workoutLog.avgHrBpm),
    };
  }

  async function saveWorkoutLog() {
    setSaving(true);
    try {
      const payload = workoutPayload();
      const response = payload.manualEntryId
        ? await meApi.updateManualWorkout(payload.manualEntryId, payload)
        : await meApi.createManualWorkout(payload);
      const nextAdaptation = response.data?.adaptation;
      setToast({ message: nextAdaptation?.ready ? "Workout saved. Chara is checking adaptation." : "Workout saved" });
      setRecordingWorkout(null);
      await loadCompletedWorkouts();
      if (nextAdaptation?.jobGroupId) {
        setWaitingJob({ jobGroupId: nextAdaptation.jobGroupId, status: "RUNNING", kind: "adaptation" });
      }
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function deleteWorkoutLog() {
    if (!workoutLog.manualEntryId) return;
    setSaving(true);
    try {
      await meApi.deleteManualWorkout(workoutLog.manualEntryId);
      setToast({ message: "Workout log deleted" });
      setRecordingWorkout(null);
      await loadCompletedWorkouts();
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  async function triggerAdaptation() {
    setSaving(true);
    try {
      const response = await meApi.adaptationTrigger();
      setCompletionData({ ...(completionData || {}), adaptation: response.data });
      if (response.data?.jobGroupId) {
        setWaitingJob({ jobGroupId: response.data.jobGroupId, status: "RUNNING", kind: "adaptation" });
      }
      setToast({ message: response.data?.ready ? "Adaptation queued" : response.data?.reason || "Current week is not ready yet" });
    } catch (error) {
      setToast({ tone: "error", message: apiErrorMessage(error) });
    } finally {
      setSaving(false);
    }
  }

  if (loading && !data) {
    return (
      <div className="user-panel user-panel-loading">
        <div className="dashboard-loading">
          <div>
            <Pill>Chara dashboard</Pill>
            <h2>Warming up your training view.</h2>
            <div className="loading-bars" aria-hidden="true">
              <span />
              <span />
              <span />
            </div>
          </div>
          <div className="dashboard-loading-mascot-stage" aria-hidden="true">
            <MascotSprite variant="run" className="dashboard-loading-mascot" alt="" />
          </div>
        </div>
      </div>
    );
  }

  if (loadError && !data) {
    return (
      <div className="user-panel">
        <Toast toast={toast} onClose={() => setToast(null)} />
        <Card title="Could not load athlete panel">
          <div className="empty-state">
            <strong>We could not load your coaching workspace.</strong>
            <p>{loadError}</p>
            <Button onClick={loadMe} loading={loading}>Retry</Button>
          </div>
        </Card>
      </div>
    );
  }

  if (waitingJob?.jobGroupId && waitingJob.kind !== "adaptation" && waitingJob.status !== "COMPLETE") {
    return (
      <div className="user-panel user-panel-waiting">
        <Toast toast={toast} onClose={() => setToast(null)} />
        <div className="loading-lab">
          <div>
            <Pill>Coach build in progress</Pill>
            <h2>Chara is assembling your athlete profile into a plan.</h2>
            <p>{waitingJob.status === "FAILED" || waitingJob.status === "TIMED_OUT" || waitingJob.status === "COMPLETE_WITH_ERRORS"
              ? waitingJob.errorMessage || "The plan build did not finish."
              : "Chara is using your athlete profile and goal to shape the first training block. This page will move you into the plan view when it is ready."}</p>
            <div className="loading-bars">
              <span />
              <span />
              <span />
            </div>
            <div className="loading-status-list" aria-label="Plan build progress">
              {planBuildSignals.map((signal) => <span key={signal}>{signal}</span>)}
            </div>
            {(waitingJob.status === "FAILED" || waitingJob.status === "TIMED_OUT" || waitingJob.status === "COMPLETE_WITH_ERRORS") && (
              <Button tone="secondary" onClick={() => setWaitingJob(null)}>Back to plan draft</Button>
            )}
          </div>
          <div className="loading-mascot-stage">
            <MascotSprite variant="run" className="loading-mascot" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="user-panel">
      <Toast toast={toast} onClose={() => setToast(null)} />
      <div className="panel-toolbar">
        <div>
          <Pill>{tab === "dashboard" ? "Dashboard" : tab === "plans" ? "Workout plans" : tab === "feedback" ? "Feedback" : "Settings"}</Pill>
          <h1>{tab === "dashboard" ? "Athlete dashboard" : tab === "user" ? "User settings" : tab === "athlete" ? "Athlete profile" : tab === "feedback" ? "Feedback" : "Workout plans"}</h1>
        </div>
        <Button tone="ghost" onClick={loadMe} disabled={loading}>Refresh</Button>
      </div>

      {tab === "dashboard" && (
        <div className="athlete-dashboard">
          <section className="athlete-hero">
            <div>
              <Pill>Athlete panel</Pill>
              <h2>{currentPlan ? currentPlan.name || "Current workout plan" : "No active plan yet"}</h2>
              <p>{currentPlan?.planSummary || "Create a plan to give Chara a current training arc to coach from."}</p>
              {!currentPlan && <Button onClick={() => setPlanWizardOpen(true)}>Create a plan</Button>}
            </div>
            <MascotSprite variant="wave" className="athlete-hero-mascot" />
          </section>
          <div className="metric-grid">
            {dashboardStats.map((stat) => (
              <div className="metric-tile" key={stat.label}>
                <span>{stat.label}</span>
                <strong>{stat.value}</strong>
              </div>
            ))}
          </div>
          <Card title="What your coach is carrying forward">
            <div style={{ display: "grid", gap: 12 }}>
              {latestTrainingReview?.summary ? (
                <div style={{ padding: 12, borderRadius: 14, background: "linear-gradient(135deg, rgba(226,251,232,0.72), rgba(255,255,255,0.92))" }}>
                  <strong>{latestTrainingReview.reviewType === "BLOCK" ? "Latest block review" : "Latest training review"}</strong>
                  <div style={{ marginTop: 5 }}>{latestTrainingReview.summary}</div>
                </div>
              ) : <div className="empty-state">Your first completed training review will appear here.</div>}
              {(athleteMemory?.intents || []).length > 0 && (
                <div>
                  <strong>Active coaching intentions</strong>
                  <div style={{ marginTop: 6, color: "var(--stone-700)" }}>
                    {(athleteMemory.intents || []).map((intent) => intent.summary).filter(Boolean).join(" · ")}
                  </div>
                </div>
              )}
              {(athleteMemory?.stimulusProfiles || []).length > 0 && (
                <div>
                  <strong>Learned training responses</strong>
                  <div style={{ marginTop: 6, color: "var(--stone-700)" }}>
                    {(athleteMemory.stimulusProfiles || []).map((stimulus) =>
                      `${stimulus.stimulusType || "STIMULUS"}: ${stimulus.responseDirection || "UNKNOWN"}`
                    ).join(" · ")}
                  </div>
                </div>
              )}
            </div>
          </Card>
          <AdaptationStatus adaptation={adaptation} waitingJob={waitingJob} onTrigger={triggerAdaptation} saving={saving} unavailable={completionData?.unavailable} />
          <WeekCalendar weekView={weekView} onOpen={setSelectedWorkoutDetail} onRecord={openWorkoutRecorder} completionByWorkoutId={completionByWorkoutId} />
          <CompletedWorkoutHistory entries={completionData?.manualWorkouts || []} onEdit={(entry) => {
            setRecordingWorkout({ workout: { workoutId: entry.personalizedWorkoutId, name: entry.name }, day: { date: entry.plannedDate } });
            setWorkoutLog({ ...emptyWorkoutLog, ...entry, actualDistanceKm: distanceInputValue(entry.actualDistance) || entry.actualDistanceKm || "", distanceUnit: entry.distanceUnit || data?.distanceUnit || "mi" });
          }} />
        </div>
      )}

      {tab === "user" && (
        <div className="settings-surface">
          <Card title="Account details">
            <div className="settings-form-grid">
              <Input label="Name" value={userForm.name || ""} onChange={(value) => setUserForm({ ...userForm, name: value })} />
              <Input label="Email" value={userForm.email || ""} disabled onChange={() => {}} />
              <Input label="Phone" value={userForm.phone || ""} onChange={(value) => setUserForm({ ...userForm, phone: value })} />
              <TimeZoneCombo
                value={userForm.timeZone || ""}
                onChange={(value) => setUserForm({ ...userForm, timeZone: value })}
              />
              <Select
                label="Measurement system"
                value={userForm.measurementSystem || data?.measurementSystem || "IMPERIAL"}
                onChange={(value) => setUserForm({ ...userForm, measurementSystem: value })}
                options={[
                  { value: "IMPERIAL", label: "Miles" },
                  { value: "METRIC", label: "Kilometers" },
                ]}
              />
              <Select
                label="Preferred distance"
                value={normalizeDistanceType(userForm.preferredDistance || "")}
                onChange={(value) => setUserForm({ ...userForm, preferredDistance: value })}
                options={DISTANCE_TYPE_OPTIONS}
              />
            </div>
            <Button onClick={saveUser} disabled={saving}>Save account details</Button>
          </Card>

          <Card title="Messaging connections">
            <div className="messaging-wallet">
              <div className="messaging-wallet-copy">
                <Pill>{preferredChannel ? `${preferredChannel} preferred` : "No preferred channel"}</Pill>
                <h2>Choose where Chara should reach you.</h2>
                <p>Connections stay saved to your account. Add Telegram or SMS once, switch the preferred channel when life changes, and remove any channel you no longer want stored.</p>
              </div>

              <div className="messaging-methods">
                {["TELEGRAM", "SMS"].map((channel) => {
                  const endpoints = messagingByChannel[channel] || [];
                  const hasEndpoint = endpoints.length > 0;
                  return (
                    <section className={`messaging-method ${preferredChannel === channel ? "preferred" : ""}`} key={channel}>
                      <div className="messaging-method-head">
                        <div>
                          <span>{channel === "SMS" ? "SMS" : "Telegram"}</span>
                          <strong>{channel === "SMS" ? "Text message" : "Bot chat"}</strong>
                        </div>
                        {preferredChannel === channel ? <Pill>Preferred</Pill> : null}
                      </div>

                      {hasEndpoint ? (
                        <div className="endpoint-card-list">
                          {endpoints.map((endpoint) => (
                            <div className="endpoint-card" key={endpoint.endpointId}>
                              <div>
                                <strong>{endpointLabel(endpoint)}</strong>
                                <span>{endpoint.status || "ACTIVE"} · {endpoint.endpointId}</span>
                              </div>
                              <div className="endpoint-actions">
                                <Button tone="ghost" disabled={messagingBusy || (preferredChannel === channel && endpoint.primaryEndpoint)} onClick={() => setPreferredEndpoint(endpoint)}>
                                  Use
                                </Button>
                                <Button tone="ghost" disabled={messagingBusy} onClick={() => deleteMessagingEndpoint(endpoint)}>
                                  Delete
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="messaging-empty-method">
                          {channel === "SMS"
                            ? "Add a phone number and consent record for Twilio SMS."
                            : "Generate a setup link, then message the bot once to bind Telegram."}
                        </div>
                      )}

                      {channel === "SMS" ? (
                        <div className="add-channel-box">
                          <Input
                            label="SMS phone"
                            value={smsDraft.phone}
                            onChange={(value) => setSmsDraft({ ...smsDraft, phone: value })}
                            placeholder="+1 555 123 4567"
                          />
                          <label className="sms-consent-check compact">
                            <input
                              type="checkbox"
                              checked={smsDraft.consent}
                              onChange={(event) => setSmsDraft({ ...smsDraft, consent: event.target.checked })}
                            />
                            <span>I agree to receive recurring SMS from Chara. Message frequency varies. Message and data rates may apply. Reply STOP to opt out.</span>
                          </label>
                          <Button tone="secondary" onClick={createSmsEndpoint} disabled={messagingBusy || !smsDraft.phone || !smsDraft.consent}>
                            Add SMS
                          </Button>
                        </div>
                      ) : (
                        <div className="add-channel-box">
                          <Button tone="secondary" onClick={createTelegramConnection} disabled={messagingBusy}>
                            Add Telegram
                          </Button>
                          {telegramLink ? (
                            <a className="telegram-link" href={telegramLink.setupUrl} target="_blank" rel="noreferrer">
                              Open @{telegramLink.botUsername} in Telegram
                            </a>
                          ) : null}
                        </div>
                      )}
                    </section>
                  );
                })}
              </div>
            </div>
          </Card>
        </div>
      )}

      {tab === "athlete" && (
        <Card title="Athlete settings" action={<Button tone="ghost" onClick={loadMe}>Refresh</Button>}>
          <div className="form-grid">
            <Input label="Max mileage" type="number" value={athleteForm.maxMileage} onChange={(value) => setAthleteForm({ ...athleteForm, maxMileage: value })} />
            <Select
              label="Training bias"
              value={athleteForm.trainingBias || "NORMAL"}
              onChange={(value) => setAthleteForm({ ...athleteForm, trainingBias: value })}
              options={[
                { value: "CAUTIOUS", label: "Cautious" },
                { value: "NORMAL", label: "Normal" },
                { value: "AGGRESSIVE", label: "Aggressive" },
              ]}
            />
          </div>
          <TextArea label="Training experience" rows={6} value={athleteForm.trainingExperience} onChange={(value) => setAthleteForm({ ...athleteForm, trainingExperience: value })} />
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 800 }}>Race results</div>
                <div style={{ color: "var(--stone-700)", fontSize: 13 }}>Track PRs and useful race marks with date and context.</div>
              </div>
              <Button
                tone="ghost"
                onClick={() => setAthleteForm({ ...athleteForm, personalRecords: [...(athleteForm.personalRecords || []), { distance: "FIVE_K", time: "", date: "", context: "" }] })}
              >
                + Add
              </Button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {(athleteForm.personalRecords || []).map((record, index) => (
                <div key={index} style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(150px, 1fr))", gap: 10, alignItems: "end" }}>
                  <Select
                    label="Distance"
                    value={normalizeDistanceType(record.distance)}
                    onChange={(value) => {
                      const next = [...(athleteForm.personalRecords || [])];
                      next[index] = { ...next[index], distance: value };
                      setAthleteForm({ ...athleteForm, personalRecords: next });
                    }}
                    options={DISTANCE_TYPE_OPTIONS}
                  />
                  <Input
                    label="Time"
                    value={record.time || ""}
                    onChange={(value) => {
                      const next = [...(athleteForm.personalRecords || [])];
                      next[index] = { ...next[index], time: value };
                      setAthleteForm({ ...athleteForm, personalRecords: next });
                    }}
                  />
                  <Input
                    label="Date"
                    type="date"
                    value={record.date || ""}
                    onChange={(value) => {
                      const next = [...(athleteForm.personalRecords || [])];
                      next[index] = { ...next[index], date: value };
                      setAthleteForm({ ...athleteForm, personalRecords: next });
                    }}
                  />
                  <Input
                    label="Context"
                    value={record.context || ""}
                    onChange={(value) => {
                      const next = [...(athleteForm.personalRecords || [])];
                      next[index] = { ...next[index], context: value };
                      setAthleteForm({ ...athleteForm, personalRecords: next });
                    }}
                  />
                  <Button tone="ghost" onClick={() => setAthleteForm({ ...athleteForm, personalRecords: (athleteForm.personalRecords || []).filter((_, itemIndex) => itemIndex !== index) })}>
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          </div>
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10, marginBottom: 10 }}>
              <div>
                <div style={{ fontWeight: 800 }}>Preferences</div>
                <div style={{ color: "var(--stone-700)", fontSize: 13 }}>Edit each coaching preference as its own saved item.</div>
              </div>
              <Button
                tone="ghost"
                onClick={() => setAthleteForm({ ...athleteForm, preferences: [...listValue(athleteForm.preferences), ""] })}
              >
                + Add
              </Button>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              {listValue(athleteForm.preferences).map((preference, index) => (
                <div
                  key={index}
                  style={{
                    display: "grid",
                    gridTemplateColumns: "minmax(0,1fr) auto",
                    gap: 10,
                    alignItems: "start",
                    padding: 12,
                    borderRadius: 14,
                    border: "1px solid rgba(25,164,99,0.14)",
                    background: "linear-gradient(135deg, rgba(226,251,232,0.72), rgba(255,255,255,0.92))",
                  }}
                >
                  <TextArea
                    label={`Preference ${index + 1}`}
                    rows={2}
                    value={preference}
                    onChange={(value) => {
                      const next = [...listValue(athleteForm.preferences)];
                      next[index] = value;
                      setAthleteForm({ ...athleteForm, preferences: next });
                    }}
                  />
                  <Button
                    tone="ghost"
                    onClick={() => setAthleteForm({ ...athleteForm, preferences: listValue(athleteForm.preferences).filter((_, itemIndex) => itemIndex !== index) })}
                  >
                    Remove
                  </Button>
                </div>
              ))}
              {listValue(athleteForm.preferences).length === 0 && (
                <div style={{ padding: 14, borderRadius: 14, background: "rgba(15,23,42,0.04)", color: "var(--stone-700)" }}>
                  No preferences saved yet.
                </div>
              )}
            </div>
          </div>
          <div className="availability-grid">
            {DAYS.map((day) => (
              <Input
                key={day}
                label={day}
                value={availabilityValue(athleteForm.availability, day)}
                onChange={(value) => setAthleteForm({
                  ...athleteForm,
                  availability: withAvailabilityDay(athleteForm.availability, day, value),
                })}
              />
            ))}
          </div>
          <Button onClick={saveAthlete} disabled={saving}>Save athlete settings</Button>
        </Card>
      )}

      {tab === "plans" && (
        <div className="plan-grid">
          <Card title="Workout plans" action={<Button tone="secondary" onClick={() => setPlanWizardOpen(true)}>New plan</Button>}>
            {!data?.workoutPlans?.length ? (
              <div className="empty-state">No workout plan exists yet. Create one to start coaching from a real training arc.</div>
            ) : (
              <div className="plan-list">
                {[...(data.workoutPlans || [])].sort(comparePlansByStartDate).map((plan) => (
                  <div key={plan.planId} className={`plan-list-item ${plan.planId === currentPlan?.planId ? "active" : ""}`}>
                    <button onClick={() => loadWeek(plan.planId, 0)}>
                      <strong>{plan.name || "Untitled plan"}</strong>
                      <span>{plan.status || "draft"} · {calendarWeekCount(plan)} weeks · starts {compactDate(plan.startDate)}</span>
                    </button>
                    {plan.planId !== currentPlan?.planId && !terminalPlan(plan) ? (
                      <Button tone="ghost" onClick={() => setCurrentPlan(plan.planId)}>Use current</Button>
                    ) : plan.planId === currentPlan?.planId ? <Pill>Current</Pill> : <Pill>{plan.status}</Pill>}
                  </div>
                ))}
              </div>
            )}
          </Card>
          <Card title={currentPlan ? "Current plan weeks" : "Current plan"}>
            {currentPlan ? (
              <>
                <div className="week-selector">
                  {Array.from({ length: totalWeeks }, (_, index) => (
                    <button key={index} className={selectedWeek === index ? "active" : ""} onClick={() => loadWeek(currentPlan.planId, index)}>
                      Week {index + 1}
                    </button>
                  ))}
                </div>
                <div className="inline-actions">
                  <Button tone="secondary" onClick={() => finishPlan(true)} disabled={saving}>Finish plan</Button>
                  <Button tone="ghost" onClick={() => finishPlan(false)} disabled={saving}>End early</Button>
                </div>
                <AdaptationStatus adaptation={adaptation} waitingJob={waitingJob} onTrigger={triggerAdaptation} saving={saving} unavailable={completionData?.unavailable} />
                <WeekCard weekView={weekView} athleteMemory={athleteMemory} onPreference={saveWorkoutPreference} onRecord={openWorkoutRecorder} onOpen={setSelectedWorkoutDetail} completionByWorkoutId={completionByWorkoutId} />
                <CompletedWorkoutHistory entries={completionData?.manualWorkouts || []} onEdit={(entry) => {
                  setRecordingWorkout({ workout: { workoutId: entry.personalizedWorkoutId, name: entry.name }, day: { date: entry.plannedDate } });
                  setWorkoutLog({ ...emptyWorkoutLog, ...entry, actualDistanceKm: distanceInputValue(entry.actualDistance) || entry.actualDistanceKm || "", distanceUnit: entry.distanceUnit || data?.distanceUnit || "mi" });
                }} />
              </>
            ) : (
              <div className="empty-state">
                <strong>No active training plan.</strong>
                <span>{latestTrainingReview?.summary || "Completed and ended plans remain available in your plan history."}</span>
              </div>
            )}
          </Card>
        </div>
      )}

      {tab === "feedback" && (
        <div className="plan-grid">
          <Card title="Send feedback">
            <div className="form-grid">
              <Select label="Type" value={feedbackForm.type} onChange={(type) => saveFeedbackForm({ ...feedbackForm, type })} options={FEEDBACK_TYPES} />
              <Select label="Severity" value={feedbackForm.severity} onChange={(severity) => saveFeedbackForm({ ...feedbackForm, severity })} options={FEEDBACK_SEVERITIES} />
              <Input label="Title" value={feedbackForm.title} onChange={(title) => saveFeedbackForm({ ...feedbackForm, title })} />
            </div>
            <TextArea label="Message" rows={6} value={feedbackForm.message} onChange={(message) => saveFeedbackForm({ ...feedbackForm, message })} />
            <Button onClick={submitFeedback} disabled={saving || !feedbackForm.message.trim()}>Send feedback</Button>
          </Card>
          <Card title="Recent feedback">
            {feedbackRecords.length ? (
              <div className="plan-list">
                {feedbackRecords.map((record) => (
                  <div key={record.feedbackId} className="plan-list-item">
                    <button type="button">
                      <strong>{record.title || record.type || "Feedback"}</strong>
                      <span>{record.status || "OPEN"} · {record.type || "OTHER"} · {compactDate(record.createdAt)}</span>
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="empty-state">Feedback you send in this session will appear here.</div>
            )}
          </Card>
        </div>
      )}

      <PlanWizardModal
        open={planWizardOpen}
        step={planWizardStep}
        setStep={setPlanWizardStep}
        draft={planDraft}
        setDraft={setPlanDraft}
        philosophies={philosophies}
        personas={personas}
        saving={saving}
        onClose={() => setPlanWizardOpen(false)}
        savedAt={planDraftSavedAt}
        onSaveDraft={savePlanDraft}
        onBuild={createPlan}
      />
      <WorkoutDetailModal
        open={Boolean(selectedWorkoutDetail)}
        workout={selectedWorkoutDetail?.workout}
        day={selectedWorkoutDetail?.day}
        completion={selectedWorkoutDetail?.workout ? completionByWorkoutId.get(selectedWorkoutDetail.workout.workoutId) : null}
        onClose={() => setSelectedWorkoutDetail(null)}
        onRecord={() => {
          if (selectedWorkoutDetail?.workout) {
            openWorkoutRecorder(selectedWorkoutDetail.workout, selectedWorkoutDetail.day);
            setSelectedWorkoutDetail(null);
          }
        }}
      />
      <WorkoutLogModal
        open={Boolean(recordingWorkout)}
        workout={recordingWorkout?.workout}
        log={workoutLog}
        setLog={setWorkoutLog}
        saving={saving}
        onClose={() => setRecordingWorkout(null)}
        onSave={saveWorkoutLog}
        onDelete={deleteWorkoutLog}
      />
    </div>
  );
}

function PlanWizardModal({ open, step, setStep, draft, setDraft, philosophies, personas, saving, savedAt, onClose, onSaveDraft, onBuild }) {
  useEffect(() => {
    if (!open) return;
    window.requestAnimationFrame(() => {
      document.querySelector(".plan-wizard")?.scrollTo({ top: 0, left: 0 });
    });
  }, [open, step]);

  if (!open) return null;
  const current = PLAN_WIZARD_STEPS[step];
  const isLast = step === PLAN_WIZARD_STEPS.length - 1;
  const hasFoundation = Boolean(String(draft.name || "").trim() && draft.startDate);
  const hasGoal = Boolean(draft.raceDistance && String(draft.goalName || "").trim() && draft.goalDate);
  const canContinue = step === 0
    ? hasFoundation
    : step === 1
      ? hasGoal
      : true;
  const canBuildPlan = hasFoundation && hasGoal;
  const selectedPersona = personas.find((persona) => persona.personaId === draft.personaId) || null;
  const defaultPhilosophyId = selectedPersona?.philosophyIdsByDistance?.[draft.raceDistance] || "";
  const defaultPhilosophy = philosophies.find((philosophy) => philosophy.philosophyId === defaultPhilosophyId);
  const philosophyOptions = philosophies.filter((philosophy) => philosophyForDistance(philosophy, draft.raceDistance)).map((philosophy) => ({
    value: philosophy.philosophyId,
    label: philosophy.name || philosophy.philosophyId,
  }));

  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Create workout plan">
      <div className="plan-wizard">
        <div className="plan-wizard-art">
          <img src={planWizardArtwork} alt="" />
        </div>
        <div className="plan-wizard-body">
          <div className="plan-wizard-head">
            <div>
              <Pill>New training block</Pill>
              <h2>{current.title}</h2>
              <p>{current.helper}</p>
            </div>
            <button type="button" className="modal-close" onClick={onClose}>Close</button>
          </div>
          <div className="wizard-progress">
            {PLAN_WIZARD_STEPS.map((item, index) => (
              <button key={item.key} className={index === step ? "active" : ""} onClick={() => setStep(index)}>{index + 1}</button>
            ))}
          </div>
          <div className="form-grid">
            {current.fields.includes("name") && <Input label="Plan name" placeholder="First half marathon build, spring race block..." value={draft.name} onChange={(value) => setDraft({ ...draft, name: value })} />}
            {current.fields.includes("startDate") && <Input label="Start date" type="date" value={draft.startDate} onChange={(value) => setDraft({ ...draft, startDate: value })} />}
            {current.fields.includes("raceDistance") && <Select label="Goal distance" value={draft.raceDistance} onChange={(value) => setDraft({ ...draft, raceDistance: value, philosophyId: "" })} options={DISTANCE_TYPE_OPTIONS} />}
            {current.fields.includes("goalName") && <Input label="Goal name" placeholder="Finish my first half marathon feeling strong..." value={draft.goalName} onChange={(value) => setDraft({ ...draft, goalName: value })} />}
            {current.fields.includes("raceName") && <Input label="Race name" placeholder="Optional race name" value={draft.raceName} onChange={(value) => setDraft({ ...draft, raceName: value })} />}
            {current.fields.includes("goalDate") && <Input label="Race date" type="date" value={draft.goalDate} onChange={(value) => setDraft({ ...draft, goalDate: value })} />}
            {current.fields.includes("personaId") && (
              <PersonaPicker
                personas={personas}
                selectedPersonaId={draft.personaId}
                distance={draft.raceDistance}
                onSelect={(personaId) => setDraft({ ...draft, personaId, philosophyId: "" })}
              />
            )}
            {current.fields.includes("philosophyId") && (
              <div className="wizard-field-span">
                <Select
                  label="Training philosophy override"
                  value={draft.philosophyId}
                  onChange={(value) => setDraft({ ...draft, philosophyId: value })}
                  options={[{ value: "", label: defaultPhilosophy ? `Use ${selectedPersona?.coachDisplayName || "coach"} default: ${defaultPhilosophy.name}` : "Use coach default for distance" }, ...philosophyOptions]}
                />
                <div className="plan-option-note">
                  {defaultPhilosophy ? `${selectedPersona?.coachDisplayName || "Your coach"} maps ${distanceTypeLabel(draft.raceDistance)} to ${defaultPhilosophy.name}. Override only when you want this plan to use a different doctrine.` : "Choose a coach that supports this distance to use persona-default philosophy hydration."}
                </div>
              </div>
            )}
            {current.fields.includes("targetTime") && <Input label="Target time" placeholder="3:10 marathon, 7 hours, finish healthy..." value={draft.targetTime} onChange={(value) => setDraft({ ...draft, targetTime: value })} />}
            {current.fields.includes("pace") && <Input label="Target pace" placeholder="Optional. Use if it matters for this goal." value={draft.pace} onChange={(value) => setDraft({ ...draft, pace: value })} />}
          </div>
          <div className="plan-actions wizard-actions">
            <div className="draft-save-group">
              <Button tone={savedAt ? "secondary" : "ghost"} onClick={onSaveDraft}>
                {savedAt ? "Draft saved" : "Save draft"}
              </Button>
              {savedAt && <span>Saved {compactDate(savedAt)}</span>}
            </div>
            <div className="wizard-nav-actions">
              {step > 0 && <Button tone="ghost" onClick={() => setStep(step - 1)}>Back</Button>}
              {!isLast ? (
                <Button onClick={() => setStep(step + 1)} disabled={!canContinue}>Next</Button>
              ) : (
                <Button onClick={onBuild} disabled={saving || !canBuildPlan}>Build plan</Button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function PersonaPicker({ personas, selectedPersonaId, distance, onSelect }) {
  if (!personas.length) {
    return <div className="empty-state wizard-field-span">Coach personas are loading.</div>;
  }
  return (
    <div className="persona-choice-grid wizard-field-span">
      {personas.map((persona) => {
        const meta = personaMeta(persona);
        const selected = persona.personaId === selectedPersonaId;
        const supported = personaSupportsDistance(persona, distance);
        return (
          <button
            key={`${persona.personaId}:${persona.version || "latest"}`}
            type="button"
            className={`persona-choice-card${selected ? " selected" : ""}`}
            onClick={() => supported && onSelect(persona.personaId)}
            disabled={!supported}
          >
            <span className="persona-choice-image">
              <img src={persona.imageUrl || meta.image} alt="" />
            </span>
            <span className="persona-choice-copy">
              <span className="persona-choice-level">{meta.level}</span>
              <strong>{persona.coachDisplayName || persona.personaId}</strong>
              <span>{meta.tagline}</span>
              <small>{supported ? meta.detail : `No ${distanceTypeLabel(distance)} philosophy is configured for this coach yet.`}</small>
            </span>
          </button>
        );
      })}
    </div>
  );
}

function AdaptationStatus({ adaptation, waitingJob, onTrigger, saving, unavailable }) {
  if (unavailable) {
    return (
      <Card title="Adaptation readiness">
        <div className="adaptation-strip">
          <div>
            <Pill>Auth required</Pill>
            <strong>Workout completion data is not available yet.</strong>
            <span>Refresh after login finishes so Chara can read your completed workouts.</span>
          </div>
        </div>
      </Card>
    );
  }
  if (!adaptation) return null;
  const missing = adaptation.missingWorkouts || [];
  const queued = waitingJob?.kind === "adaptation" && waitingJob?.jobGroupId;
  return (
    <Card title="Adaptation readiness" action={<Button tone="secondary" onClick={onTrigger} disabled={saving}>Adapt now</Button>}>
      <div className="adaptation-strip">
        <div>
          <Pill>{queued ? "Queued" : adaptation.ready ? "Ready" : "Needs workouts"}</Pill>
          <strong>
            Current week: {adaptation.accountedWorkoutCount || 0}/{adaptation.plannedWorkoutCount || 0} accounted for
          </strong>
          <span>{queued ? `Job ${waitingJob.jobGroupId}` : adaptation.reason || "Chara can adapt next week once the current week is complete."}</span>
        </div>
        {missing.length > 0 && (
          <div className="missing-workouts">
            {missing.slice(0, 4).map((item) => <span key={item}>{normalizeMissingWorkoutLabel(item)}</span>)}
          </div>
        )}
      </div>
    </Card>
  );
}

function CompletedWorkoutHistory({ entries, onEdit }) {
  return (
    <Card title="Completed workouts" action={<Pill>{entries.length} manual</Pill>}>
      {!entries.length ? (
        <div className="empty-state">No manual workout logs yet. Record a planned workout to give Chara adaptation data.</div>
      ) : (
        <div className="completed-history">
          {entries.slice(0, 8).map((entry) => (
            <button key={entry.manualEntryId} onClick={() => onEdit(entry)}>
              <strong>{entry.name || "Workout"}</strong>
              <span>{entry.completionStatus || "COMPLETED"} · {compactDate(entry.startedAt || entry.plannedDate)} · {actualDistanceLabel(entry)}{entry.actualPace ? ` · ${entry.actualPace}` : ""}</span>
              <WorkoutIntervalSummary intervals={entry.intervals} compact />
            </button>
          ))}
        </div>
      )}
    </Card>
  );
}

function WorkoutIntervalSummary({ intervals, compact = false }) {
  const visible = Array.isArray(intervals) ? intervals.filter(Boolean) : [];
  if (!visible.length) return null;
  const shown = compact ? visible.slice(0, 4) : visible;
  return (
    <div className={compact ? "interval-summary compact" : "interval-summary"}>
      {shown.map((interval, index) => (
        <div key={`${interval.intervalIndex || index}-${interval.type || "interval"}`}>
          <strong>{interval.type || `Interval ${interval.intervalIndex || index + 1}`}</strong>
          <span>{intervalDetail(interval)}</span>
        </div>
      ))}
      {compact && visible.length > shown.length && <em>+ {visible.length - shown.length} more interval{visible.length - shown.length === 1 ? "" : "s"}</em>}
    </div>
  );
}

function WorkoutLogModal({ open, workout, log, setLog, saving, onClose, onSave, onDelete }) {
  if (!open) return null;
  const distanceUnit = log.distanceUnit || workout?.distanceUnit || "mi";
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Record workout">
      <div className="workout-log-modal">
        <div className="plan-wizard-head">
          <div>
            <Pill>{log.manualEntryId ? "Update actual" : "Record workout"}</Pill>
            <h2>{workoutLabel(workout)}</h2>
            <p>Capture what actually happened so Chara can adapt from real training, not the ideal version of the week.</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>Close</button>
        </div>
        <div className="planned-workout-preview">
          {workout?.estimatedDuration && <span>Planned duration: {workout.estimatedDuration}</span>}
          {workout?.estimatedDistance ? <span>Planned distance: {workoutDistanceLabel(workout, distanceUnit)}</span> : null}
          {workout?.notes && <p>{workout.notes}</p>}
        </div>
        <div className="form-grid">
          <Select label="Status" value={log.completionStatus} onChange={(value) => setLog({ ...log, completionStatus: value })} options={["COMPLETED", "PARTIAL", "SKIPPED"]} />
          <Input label="Started at" value={log.startedAt || ""} onChange={(value) => setLog({ ...log, startedAt: value })} placeholder="2026-05-06T12:00:00Z" />
          <Input label={`Distance (${distanceUnit})`} type="number" value={log.actualDistanceKm ?? ""} onChange={(value) => setLog({ ...log, actualDistanceKm: value })} />
          <Input label={`Pace (${distanceUnit})`} value={log.actualPace || ""} onChange={(value) => setLog({ ...log, actualPace: value })} placeholder={distanceUnit === "mi" ? "8:00/mi" : "5:00/km"} />
          <Input label="Duration" value={log.actualDuration || ""} onChange={(value) => setLog({ ...log, actualDuration: value })} placeholder="0:45:00" />
          <Input label="Moving time (s)" type="number" value={log.movingTimeSeconds ?? ""} onChange={(value) => setLog({ ...log, movingTimeSeconds: value })} />
          <Input label="Avg HR" type="number" value={log.avgHrBpm ?? ""} onChange={(value) => setLog({ ...log, avgHrBpm: value })} />
          <Input label="Elevation (m)" type="number" value={log.elevationGainM ?? ""} onChange={(value) => setLog({ ...log, elevationGainM: value })} />
        </div>
        {log.completionStatus === "SKIPPED" && (
          <TextArea label="Skip reason" rows={3} value={log.skipReason || ""} onChange={(value) => setLog({ ...log, skipReason: value })} />
        )}
        <WorkoutIntervalSummary intervals={log.intervals} />
        <TextArea label="Athlete notes" rows={4} value={log.athleteNotes || ""} onChange={(value) => setLog({ ...log, athleteNotes: value })} placeholder="How did it feel? What changed? Any soreness or fatigue?" />
        <div className="plan-actions wizard-actions">
          <div>
            {log.manualEntryId && <Button tone="ghost" onClick={onDelete} disabled={saving}>Delete</Button>}
          </div>
          <div className="wizard-nav-actions">
            <Button tone="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={onSave} disabled={saving}>Save workout</Button>
          </div>
        </div>
      </div>
    </div>
  );
}

function WorkoutDetailModal({ open, workout, day, completion, onClose, onRecord }) {
  if (!open) return null;
  return (
    <div className="modal-backdrop" role="dialog" aria-modal="true" aria-label="Workout details">
      <div className="workout-log-modal workout-detail-modal">
        <div className="plan-wizard-head">
          <div>
            <Pill>{day?.date ? compactDate(day.date) : "Workout"}</Pill>
            <h2>{workoutLabel(workout)}</h2>
            <p>{workout?.primaryTrainingBenefit || workout?.notes || "Review the planned work before you record what happened."}</p>
          </div>
          <button type="button" className="modal-close" onClick={onClose}>Close</button>
        </div>
        <div className="workout-detail-grid">
          {workoutDetails(workout).map(([label, value]) => (
            <div key={label}>
              <span>{label}</span>
              <strong>{value}</strong>
            </div>
          ))}
          {completion && (
            <div>
              <span>Status</span>
              <strong>{completion.completionStatus || "COMPLETED"}</strong>
            </div>
          )}
        </div>
        {Array.isArray(workout?.steps) && workout.steps.length > 0 && (
          <div className="workout-steps">
            {workout.steps.map((step, index) => (
              <div key={`${index}-${step.name || step.description || "step"}`}>
                <strong>{step.name || `Step ${index + 1}`}</strong>
                <span>{step.description || step.duration || step.distance || JSON.stringify(step)}</span>
              </div>
            ))}
          </div>
        )}
        {workout?.notes && <div className="planned-workout-preview"><p>{workout.notes}</p></div>}
        <div className="plan-actions wizard-actions">
          <Button tone="ghost" onClick={onClose}>Close</Button>
          <Button onClick={onRecord}>{completion ? "Update actual" : "Record workout"}</Button>
        </div>
      </div>
    </div>
  );
}

function WeekCalendar({ weekView, onOpen, onRecord, completionByWorkoutId }) {
  if (!weekView) {
    return <Card title="Current week"><div className="empty-state">No generated workouts to show yet.</div></Card>;
  }
  return (
    <Card title={`Current week ${Number(weekView.weekIndex || 0) + 1}`} tone="accent">
      <div className="dashboard-calendar">
        {(weekView.days || []).map((day) => (
          <div className="calendar-day-card" key={day.day}>
            <div className="week-day-head">
              <strong>{dayLabel(day)}</strong>
              <span>{compactDate(day.date)}</span>
            </div>
            {!day.workouts?.length ? (
              <div className="empty-state">Rest</div>
            ) : day.workouts.map((workout) => {
              const completion = completionByWorkoutId?.get(workout.workoutId);
              const autoCompleted = isAutoCompletedWorkout(workout);
              return (
                <div
                  role="button"
                  tabIndex={0}
                  className={autoCompleted ? "calendar-workout-card auto-completed" : "calendar-workout-card"}
                  key={workout.workoutId}
                  onClick={() => onOpen({ workout, day })}
                  onKeyDown={(event) => {
                    if (event.key === "Enter" || event.key === " ") onOpen({ workout, day });
                  }}
                >
                  <strong>{workoutLabel(workout)}</strong>
                  <span>{workout.estimatedDuration || workoutDistanceLabel(workout) || "Distance TBD"} · {workout.primaryTrainingBenefit || "Training support"}</span>
                  <div>
                    {autoCompleted ? <Pill>Auto-complete</Pill> : completion && <Pill>{completion.completionStatus || "COMPLETED"}</Pill>}
                    {!autoCompleted && (
                      <button type="button" onClick={(event) => {
                        event.stopPropagation();
                        onRecord(workout, day);
                      }}>
                        {completion ? "Update" : "Record"}
                      </button>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}

function WeekCard({ weekView, athleteMemory, onPreference, onRecord, onOpen, completionByWorkoutId }) {
  const [feedbackDraft, setFeedbackDraft] = useState(null);
  const preferences = athleteMemory?.workoutPreferences || {};
  const liked = new Set(Object.keys(preferences).filter((workoutId) => preferences[workoutId] === "like"));
  const disliked = new Set(Object.keys(preferences).filter((workoutId) => preferences[workoutId] === "dislike"));
  if (!weekView) {
    return <Card title="Current week"><div className="empty-state">No generated workouts to show yet.</div></Card>;
  }
  return (
    <Card title={`Week ${Number(weekView.weekIndex || 0) + 1}`} tone="accent">
      <div className="week-days">
        {(weekView.days || []).map((day) => (
          <div className="week-day" key={day.day}>
            <div className="week-day-head">
              <strong>{dayLabel(day)}</strong>
              <span>{compactDate(day.date)}</span>
            </div>
            {!day.workouts?.length ? <div className="empty-state">Rest or open day</div> : day.workouts.map((workout) => {
              const completion = completionByWorkoutId?.get(workout.workoutId);
              const autoCompleted = isAutoCompletedWorkout(workout);
              return (
                <div className={autoCompleted ? "workout-row auto-completed" : "workout-row"} key={workout.workoutId}>
                  <button type="button" className="workout-row-main" onClick={() => onOpen?.({ workout, day })}>
                    <strong>{workoutLabel(workout)}</strong>
                    <span>{workout.estimatedDuration || workoutDistanceLabel(workout) || "Distance TBD"} · {workout.primaryTrainingBenefit || workout.notes || "Training support"}</span>
                    {autoCompleted ? <Pill>Auto-complete</Pill> : completion && <Pill>{completion.completionStatus || "COMPLETED"}</Pill>}
                  </button>
                  <div className="workout-actions">
                    <button onClick={() => onOpen?.({ workout, day })}>Details</button>
                    {!autoCompleted && <button onClick={() => onRecord(workout, day)}>{completion ? "Update actual" : "Record workout"}</button>}
                    <button className={liked.has(workout.workoutId) ? "active" : ""} onClick={() => completion
                      ? setFeedbackDraft({ workoutId: workout.workoutId, preference: "like", reason: "" })
                      : onPreference(workout.workoutId, "like")}>Like</button>
                    <button className={disliked.has(workout.workoutId) ? "active dislike" : ""} onClick={() => completion
                      ? setFeedbackDraft({ workoutId: workout.workoutId, preference: "dislike", reason: "" })
                      : onPreference(workout.workoutId, "dislike")}>Dislike</button>
                  </div>
                  {feedbackDraft?.workoutId === workout.workoutId && (
                    <div className="workout-feedback-why">
                      <label htmlFor={`feedback-${workout.workoutId}`}>What stood out? <span>Optional</span></label>
                      <textarea id={`feedback-${workout.workoutId}`} rows="2" maxLength="280"
                        value={feedbackDraft.reason}
                        onChange={(event) => setFeedbackDraft({ ...feedbackDraft, reason: event.target.value })}
                        placeholder="A quick note helps your coach learn what worked." />
                      <div>
                        <button onClick={() => setFeedbackDraft(null)}>Cancel</button>
                        <button className="primary" onClick={() => {
                          onPreference(workout.workoutId, feedbackDraft.preference, feedbackDraft.reason);
                          setFeedbackDraft(null);
                        }}>Save feedback</button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ))}
      </div>
    </Card>
  );
}
